/**
 * Base accessorial builder function
 */

import { AccessorialStatus, type AccessorialStatusCode } from '../../constants/status-codes.js'
import type { Accessorial } from '../../types/accessorial.js'
import type { BuildAccessorialOptions } from './types/builderOptions.js'

/**
 * Build a generalized accessorial object (using database schema)
 *
 * This is the base builder used by specific accessorial builders for consistency.
 * It validates inputs, rounds amounts to 2 decimal places, and returns an
 * insertable accessorial object.
 *
 * @param options - Accessorial build options
 * @returns Insertable accessorial object
 * @throws Error if validation fails
 *
 * @example
 * ```typescript
 * const accessorial = buildAccessorial({
 *   moveId: 12345,
 *   code: 'delay',
 *   status: 'approved',
 *   cost: 0,
 *   ar_amount: 18.15,
 *   ap_amount: 9.35,
 *   notes: 'Driver delay at pickup'
 * })
 * ```
 */
export function buildAccessorial(options: BuildAccessorialOptions): Accessorial {
  // Validation for required props
  if (!options || typeof options !== 'object') {
    throw new Error('Failed to build accessorial: Invalid options prop!')
  }
  if (!options.moveId || (typeof options.moveId !== 'number' && typeof options.moveId !== 'bigint')) {
    throw new Error('Failed to build accessorial: Invalid moveId prop!')
  }
  if (!options.code || typeof options.code !== 'string') {
    throw new Error('Failed to build accessorial: Invalid code prop!')
  }

  // Validation for optional props
  if (options.cost !== undefined && typeof options.cost !== 'number') {
    throw new Error('Failed to build accessorial: Invalid cost prop!')
  }
  if (options.ar_amount !== undefined && typeof options.ar_amount !== 'number') {
    throw new Error('Failed to build accessorial: Invalid ar_amount prop!')
  }
  if (options.ap_amount !== undefined && typeof options.ap_amount !== 'number') {
    throw new Error('Failed to build accessorial: Invalid ap_amount prop!')
  }
  if (options.notes !== undefined && options.notes !== null && typeof options.notes !== 'string') {
    throw new Error('Failed to build accessorial: Invalid notes prop!')
  }
  if (options.status && typeof options.status !== 'string') {
    throw new Error('Failed to build accessorial: Invalid status prop!')
  }

  // Status codes validation
  const validStatusCodes = Object.values(AccessorialStatus).map((status) => status.code)
  if (options.status && !validStatusCodes.includes(options.status as AccessorialStatusCode)) {
    throw new Error(`Failed to build accessorial: Status prop ${options.status} is not a valid status!`)
  }

  // Round all numbers to 2 decimal places to avoid floating point errors
  // NOTE: We use toFixed here instead of roundToNearestPenny to match legacy behavior
  // which only rounds during merge operations
  const cost = options.cost !== undefined ? Number(options.cost.toFixed(2)) : 0
  const ar_amount = options.ar_amount !== undefined ? Number(options.ar_amount.toFixed(2)) : 0
  const ap_amount = options.ap_amount !== undefined ? Number(options.ap_amount.toFixed(2)) : 0
  const notes = options.notes !== undefined ? options.notes : null
  const status = options.status || AccessorialStatus.APPROVED.code

  // Build accessorial
  const newAccessorial: Accessorial = {
    move_id: options.moveId,
    code: options.code,
    status,
    cost,
    ar_amount,
    ap_amount,
    notes,
  }

  return newAccessorial
}
